import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { EnvelopeSimple, Globe } from "@phosphor-icons/react";
import api from "../../lib/api";

function DomainRow({ domain, onVerify }) {
  return (
    <div className="border-b border-zinc-100 px-4 py-3 flex items-center justify-between">
      <div>
        <div className="font-mono text-sm">{domain.name}</div>
        <div className="text-[10px] text-zinc-500 mt-0.5">ID {domain.id} · created {domain.created_at}</div>
      </div>
      <div className="flex items-center gap-3">
        <span className={`text-[10px] px-1.5 py-0.5 ${domain.status === "verified" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
          {(domain.status || "pending").toUpperCase()}
        </span>
        <button onClick={() => onVerify(domain.id)} className="text-[10px] font-mono text-zinc-700 hover:text-[#FF3E00]" data-testid={`domain-verify-${domain.id}`}>
          RE-CHECK
        </button>
      </div>
    </div>
  );
}

export default function EmailDomainsPanel() {
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [testTo, setTestTo] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/email/domains");
      setDomains(data?.data || data || []);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("EmailDomainsPanel: /admin/email/domains unavailable", err);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const addDomain = async () => {
    if (!newDomain) return;
    try {
      await api.post("/admin/email/domains", { domain: newDomain });
      toast.success(`Domain ${newDomain} added — copy DNS records to your DNS provider`);
      setNewDomain("");
      refresh();
    } catch (e) {
      toast.error("Failed: " + (e.response?.data?.detail?.message || JSON.stringify(e.response?.data?.detail)));
    }
  };

  const verifyDomain = async (id) => {
    try {
      const { data } = await api.post(`/admin/email/domains/${id}/verify`);
      toast.success(`Verify check triggered. Status: ${data?.status || "pending"}`);
      refresh();
    } catch (e) {
      toast.error("Verify failed");
    }
  };

  const sendTest = async () => {
    if (!testTo) return;
    try {
      const { data } = await api.get(`/admin/email/test?to=${encodeURIComponent(testTo)}`);
      if (data.delivered) toast.success(`Test email sent to ${testTo} (id ${data.id})`);
      else toast.error(`Send failed: ${data.error || "unknown"}`);
    } catch (e) {
      toast.error("Send failed");
    }
  };

  return (
    <section className="border border-zinc-950 mt-8" data-testid="email-domains-panel">
      <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center gap-2">
        <Globe size={12} /> email.delivery (resend)
      </div>
      <div className="p-6">
        <p className="text-sm text-zinc-600 mb-4 max-w-2xl">
          Manage your sending domains. Add a domain, then update the DNS records shown to verify it.
          Until verified, outbound email falls back to logs (only Resend&apos;s default sender works to your own account).
        </p>

        <div className="flex items-end gap-3 mb-6">
          <div className="flex-1 max-w-md">
            <label className="label-sm text-zinc-500">Sending domain</label>
            <input value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="smarterp-st.com" data-testid="domain-input" className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none font-mono" />
          </div>
          <button onClick={addDomain} className="bg-zinc-950 text-white text-xs font-semibold px-5 py-2.5 hover:bg-[#FF3E00]" data-testid="domain-add-btn">
            ADD DOMAIN
          </button>
        </div>

        <div className="border border-zinc-200" data-testid="domains-list">
          {domains.length === 0 && <div className="px-4 py-6 text-center text-xs font-mono text-zinc-400">NO DOMAINS REGISTERED</div>}
          {domains.map((d) => (
            <DomainRow key={d.id} domain={d} onVerify={verifyDomain} />
          ))}
        </div>

        <div className="mt-6 pt-6 border-t border-zinc-200">
          <div className="label-sm text-zinc-500 mb-2">Send a test email</div>
          <div className="flex items-end gap-3">
            <div className="flex-1 max-w-md">
              <input value={testTo} onChange={(e) => setTestTo(e.target.value)} type="email" placeholder="you@your-email.com" data-testid="test-email-input" className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none" />
            </div>
            <button onClick={sendTest} className="border border-zinc-300 hover:border-zinc-950 text-xs font-semibold px-4 py-2" data-testid="test-email-btn">
              <EnvelopeSimple size={12} className="inline mr-1" /> SEND TEST
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
